import { Field } from 'formik'
import { map } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { formatPrice } from 'shared-constants'
import styled from 'styled-components'
import { Callout, CALLOUT_INTENT, CALLOUT_VARIANT } from 'components/Callout'
import { CHECKOUT_FIELD_NAMES } from 'components/Checkout/constants'
import { Delimiter } from 'components/Delimiter'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Box, Flex } from 'components/Layout'
import { RadioGroup, RADIO_SIZE } from 'components/Radio'
import { Text } from 'components/Typography'
import { CART_SHIPMENT_TYPES } from 'constants/common'
import { COLOR_INTENT, FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'

const StyledText = styled(Text)`
  white-space: nowrap;
`

export const ShippingMethodList = ({
  shippingMethods,
  sourceIdAndMethodType,
}) => {
  if (!shippingMethods.length) {
    return (
      <Callout
        intent={CALLOUT_INTENT.INFO}
        variant={CALLOUT_VARIANT.ROUNDED_BOTTOM}
        icon={() => (
          <Icon.Info size={ICON_SIZE.PX_20} color={COLOR_INTENT.GRAY_70} />
        )}
        alignItems="center"
        py={SPACE.PX_10}
      >
        <Text
          fontSize={FONT_SIZE.PX_13}
          fontWeight={FONT_WEIGHT.MEDIUM}
          role="alert"
        >
          {`We're unable to calculate your shipping at this time. Don't worry —
          you can proceed with placing your order and a Logistics Specialist
          will be in touch shortly with a shipping quote!`}
        </Text>
      </Callout>
    )
  }
  return (
    <>
      <Delimiter />
      <Field
        name={`${CHECKOUT_FIELD_NAMES.SHIPPING_METHOD}['${sourceIdAndMethodType}']`}
      >
        {({
          field: { name, value },
          form: { setFieldValue, touched, isSubmitting },
        }) => (
          <>
            {map(
              shippingMethods,
              ({
                id: shippingMethodId,
                price,
                __typename,
                shipmentMethodTitle,
                shipmentMethodDescription,
                shipmentValue,
              }) => {
                const isVendorShipping =
                  __typename === CART_SHIPMENT_TYPES.VendorShipping
                const isDisabled = isSubmitting || isVendorShipping

                return (
                  <Box key={shippingMethodId} mx={SPACE.PX_20} my={SPACE.PX_15}>
                    <RadioGroup.Item
                      name={name}
                      value={shipmentValue}
                      isDisabled={isDisabled}
                      size={RADIO_SIZE.M}
                      groupValue={value}
                      onChange={() => {
                        setFieldValue(name, shipmentValue)
                      }}
                      isFullWidth
                    >
                      <Flex
                        ml={SPACE.PX_10}
                        flexGrow="1"
                        flexDirection="column"
                      >
                        <Text fontWeight={FONT_WEIGHT.MEDIUM}>
                          {shipmentMethodTitle}
                        </Text>
                        {shipmentMethodDescription && (
                          <Text>{shipmentMethodDescription}</Text>
                        )}
                      </Flex>
                      <Flex>
                        <StyledText pl={SPACE.PX_15}>
                          {price?.total > 0
                            ? formatPrice({
                                price: price.base,
                                currency: price.currencyIsoCode,
                              })
                            : 'FREE'}
                        </StyledText>
                      </Flex>
                    </RadioGroup.Item>
                  </Box>
                )
              }
            )}
            {touched?.shippingMethod && !value && (
              <Callout
                intent={CALLOUT_INTENT.INFO}
                variant={CALLOUT_VARIANT.ROUNDED_BOTTOM}
                icon={() => (
                  <Icon.Info
                    size={ICON_SIZE.PX_20}
                    color={COLOR_INTENT.GRAY_70}
                  />
                )}
                alignItems="center"
                py={SPACE.PX_10}
              >
                <Text
                  fontSize={FONT_SIZE.PX_13}
                  fontWeight={FONT_WEIGHT.MEDIUM}
                >
                  Select shipping method for these order items
                </Text>
              </Callout>
            )}
          </>
        )}
      </Field>
    </>
  )
}

ShippingMethodList.propTypes = {
  shippingMethods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      services: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string.isRequired,
          subtype: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          subSubtype: PropTypes.string.isRequired,
        }).isRequired
      ),
      price: PropTypes.shape({
        total: PropTypes.number.isRequired,
        currencyIsoCode: PropTypes.string.isRequired,
      }).isRequired,
    })
  ),
  sourceIdAndMethodType: PropTypes.string.isRequired,
}
